import { isNordics } from '@/utils/country-helper';
import { paths } from '@/constants/paths';
import { IMAGE_PATHS } from './constants';
import {
  Compatibility,
  ICQuestions,
  ICStepConfig,
  ICStepName,
  IntroAnswer,
  RealEstateAnswer,
  LockTypeAnswer,
  LockManufacturerAnswer,
  CylinderTypeAnswer,
  EmergencyFeatureAnswer,
  DoorDirectionAnswer,
  DoorDistanceAnswer30,
  DoorDistanceAnswer40,
  BadCompatibilityAnswer,
  B2BPropertiesAnswer,
  RealEstateUsageAnswer,
  CylinderTypeNordicsAnswer,
  NordicsInsideAnswer,
  LatchTypeNordicsAnswer,
  DoorDistanceAnswer35,
  KeyDimensionsAnswer,
  CylinderOverlapAnswer,
  CylinderExchangePossibleAnswer,
  MoreCylindersUSAnswer,
  USLockManufacturerAnswer,
  USBadCompatibilityAnswer,
} from './types';
import { getEuResultStep, getUsResultStep } from './utils';

export const installationCheckStepsConfig: ICStepConfig[] = [
  {
    stepName: ICStepName.Intro,
    mainText: ICQuestions.IC_SURVEY_INTRO,
    type: 'initialScreen',
    image: IMAGE_PATHS.INTRO,
    button: { text: 'Find your solution', value: IntroAnswer.START },
    subtext: 'Take three minutes',
    nextStep: async ({ country }) => {
      if (country === 'us') {
        return ICStepName.SingleCylinderDeadboltUS;
      }

      return ICStepName.RealEstate;
    },
  },
  // House / Apartment
  {
    stepName: ICStepName.RealEstate,
    question: ICQuestions.IC_SURVEY_REAL_ESTATE,
    type: 'imageSelect',
    options: [
      {
        text: 'House',
        value: RealEstateAnswer.HOUSE,
        image: IMAGE_PATHS.HOUSE,
      },
      {
        text: 'Apartment',
        value: RealEstateAnswer.APARTMENT,
        image: IMAGE_PATHS.APARTMENT,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ country }) => {
      if (isNordics(country)) {
        return ICStepName.CylinderTypeNordics;
      }

      return ICStepName.CylinderType;
    },
  },
  // Rental / Private
  {
    stepName: ICStepName.RealEstateUsage,
    question: ICQuestions.IC_SURVEY_REAL_ESTATE_USAGE,
    type: 'imageSelect',
    options: [
      {
        text: 'Rental',
        value: RealEstateUsageAnswer.SHORT_RENTAL,
        image: IMAGE_PATHS.RENTAL,
      },
      {
        text: 'Private',
        value: RealEstateUsageAnswer.PRIVATE,
        image: IMAGE_PATHS.PRIVATE,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer, formValues, country }) => {
      // US users skip B2B, go directly to US result
      if (country === 'us') {
        return getUsResultStep(formValues!);
      }

      if (answer === RealEstateUsageAnswer.SHORT_RENTAL) {
        return ICStepName.B2BProperties;
      }

      return getEuResultStep(formValues!);
    },
  },
  // Lock type
  {
    stepName: ICStepName.LockType,
    question: ICQuestions.IC_SURVEY_LOCK_TYPE,
    type: 'imageSelect',
    options: [
      {
        text: 'Knob',
        value: LockTypeAnswer.KNOB,
        image: IMAGE_PATHS.KNOB,
      },
      {
        text: 'Key',
        value: LockTypeAnswer.KEY,
        image: IMAGE_PATHS.KEY,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      if (answer === (LockTypeAnswer.KNOB as string)) {
        return ICStepName.LockManufacturer;
      }

      return ICStepName.EmergencyFeature;
    },
  },
  // Lock manufacturer
  {
    stepName: ICStepName.LockManufacturer,
    question: ICQuestions.IC_SURVEY_LOCK_MANUFACTURER,
    type: 'dropdownSelect',
    options: [
      { text: 'Abus', value: LockManufacturerAnswer.ABUS },
      { text: 'Evva', value: LockManufacturerAnswer.EVVA },
      { text: 'Dom', value: LockManufacturerAnswer.DOM },
      { text: 'Dormakaba', value: LockManufacturerAnswer.DORMAKABA },
      { text: 'Keso', value: LockManufacturerAnswer.KESO },
      { text: 'Nemef', value: LockManufacturerAnswer.NEMEF },
      { text: 'Yale', value: LockManufacturerAnswer.YALE },
      { text: 'Mauer', value: LockManufacturerAnswer.MAUER },
      { text: 'Other', value: LockManufacturerAnswer.OTHER },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer, formValues }) => {
      const cylinderType = formValues?.icSurvey_cylinderType as CylinderTypeAnswer;

      if (answer === LockManufacturerAnswer.OTHER) {
        if ([CylinderTypeAnswer.EURO, CylinderTypeAnswer.SWISS].includes(cylinderType)) {
          return ICStepName.DoorDirection;
        }

        return ICStepName.BadCompatibility;
      }

      return ICStepName.DoorDirection;
    },
  },
  // Emergency feature
  {
    stepName: ICStepName.EmergencyFeature,
    question: ICQuestions.IC_SURVEY_EMERGENCY_FEATURE,
    type: 'imageSelect',
    mainClip: IMAGE_PATHS.EMERGENCY_CLIP,
    options: [
      { text: 'No', value: EmergencyFeatureAnswer.NO },
      { text: 'Yes', value: EmergencyFeatureAnswer.YES },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      // skip overlap and key dimensions
      if (answer === EmergencyFeatureAnswer.NO) {
        return ICStepName.DoorDirection;
      }

      return ICStepName.CylinderOverlap;
    },
  },
  // Cylinder type
  {
    stepName: ICStepName.CylinderType,
    question: ICQuestions.IC_SURVEY_CYLINDER_TYPE,
    type: 'imageSelect',
    options: [
      {
        text: 'Euro',
        value: CylinderTypeAnswer.EURO,
        image: IMAGE_PATHS.EURO,
      },
      {
        text: 'Swiss',
        value: CylinderTypeAnswer.SWISS,
        image: IMAGE_PATHS.SWISS,
      },
      {
        text: 'UK Oval',
        value: CylinderTypeAnswer.UK_OVAL,
        image: IMAGE_PATHS.UK_OVAL,
      },
      {
        text: 'Other',
        value: CylinderTypeAnswer.OTHER,
        image: IMAGE_PATHS.OTHER,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      // If the user has selected "Other" as the cylinder type, incompatible
      if (answer === CylinderTypeAnswer.OTHER) {
        return ICStepName.BadCompatibility;
      }

      return ICStepName.LockType;
    },
  },
  // Cylinder type nordics
  {
    stepName: ICStepName.CylinderTypeNordics,
    question: ICQuestions.IC_SURVEY_CYLINDER_TYPE_NORDICS,
    type: 'imageSelect',
    options: [
      {
        text: 'Euro',
        value: CylinderTypeNordicsAnswer.EURO,
        image: IMAGE_PATHS.EURO,
      },
      {
        text: 'Round profile',
        value: CylinderTypeNordicsAnswer.NORDIC_ROUND,
        image: IMAGE_PATHS.NORDIC_ROUND,
      },
      {
        text: 'Scandic oval',
        value: CylinderTypeNordicsAnswer.SCANDIC_OVAL,
        image: IMAGE_PATHS.SCANDIC_OVAL,
      },
      {
        text: 'Other',
        value: CylinderTypeNordicsAnswer.OTHER,
        image: IMAGE_PATHS.OTHER,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      if (answer === CylinderTypeNordicsAnswer.EURO) {
        return ICStepName.LockType;
      }

      if (
        [CylinderTypeNordicsAnswer.NORDIC_ROUND, CylinderTypeNordicsAnswer.SCANDIC_OVAL].includes(
          answer as CylinderTypeNordicsAnswer,
        )
      ) {
        return ICStepName.NordicsInside;
      }

      // Other
      return ICStepName.BadCompatibility;
    },
  },
  // Nordics inside
  {
    stepName: ICStepName.NordicsInside,
    question: ICQuestions.IC_SURVEY_NORDICS_INSIDE,
    type: 'imageSelect',
    options: [
      {
        text: 'Key',
        value: NordicsInsideAnswer.KEY,
        image: IMAGE_PATHS.NORDICS_KEY,
      },
      {
        text: 'Thumbturn',
        value: NordicsInsideAnswer.THUMBTURN,
        image: IMAGE_PATHS.THUMBTURN,
      },
      {
        text: 'Combined',
        value: NordicsInsideAnswer.COMBINED,
        image: IMAGE_PATHS.COMBINED,
      },
      {
        text: 'Other',
        value: NordicsInsideAnswer.OTHER,
        image: IMAGE_PATHS.NORDICS_OTHER,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      if (
        [NordicsInsideAnswer.COMBINED, NordicsInsideAnswer.OTHER].includes(
          answer as NordicsInsideAnswer,
        )
      ) {
        return ICStepName.BadCompatibility;
      }

      return ICStepName.LatchTypeNordics;
    },
  },
  // Nordics latch type
  {
    stepName: ICStepName.LatchTypeNordics,
    question: ICQuestions.IC_SURVEY_LATCH_TYPE_NORDICS,
    description: 'nordicsLatchDescription',
    type: 'imageSelect',
    mainClip: IMAGE_PATHS.NORDICS_LATCH_TYPE,
    options: [
      { text: 'No', value: LatchTypeNordicsAnswer.NO_DEADBOLT },
      { text: 'Yes', value: LatchTypeNordicsAnswer.DEADBOLT },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      if (answer === LatchTypeNordicsAnswer.NO_DEADBOLT) {
        return ICStepName.BadCompatibility;
      }

      return ICStepName.DoorDirection;
    },
  },
  {
    stepName: ICStepName.CylinderOverlap,
    question: ICQuestions.IC_SURVEY_CYLINDER_OVERLAP,
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.CYLINDER_OVERLAP,
    options: [
      { text: 'Over 3mm', value: CylinderOverlapAnswer.OVER_3 },
      { text: 'Up to 3mm', value: CylinderOverlapAnswer.UP_TO_3 },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async () => ICStepName.KeyDimensions,
  },
  // Door direction
  {
    stepName: ICStepName.DoorDirection,
    question: ICQuestions.IC_SURVEY_DOOR_DIRECTION,
    type: 'imageSelect',
    options: [
      {
        text: 'Inwards',
        value: DoorDirectionAnswer.INWARDS,
        image: IMAGE_PATHS.INWARDS,
      },
      {
        text: 'Outwards',
        value: DoorDirectionAnswer.OUTWARDS,
        image: IMAGE_PATHS.OUTWARDS,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer, formValues }) => {
      if (answer === DoorDirectionAnswer.INWARDS) {
        return ICStepName.DoorDistance3O;
      }

      // if cylinder type is Nordics
      if (
        [CylinderTypeNordicsAnswer.SCANDIC_OVAL, CylinderTypeNordicsAnswer.NORDIC_ROUND].includes(
          formValues?.icSurvey_cylinderTypeNordics as CylinderTypeNordicsAnswer,
        )
      ) {
        return ICStepName.DoorDistance35;
      }

      return ICStepName.DoorDistance40;
    },
  },
  // Door distance 30
  {
    stepName: ICStepName.DoorDistance3O,
    question: ICQuestions.IC_SURVEY_DOOR_DISTANCE_30,
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.DOOR_DISTANCE,
    options: [
      { text: 'No', value: DoorDistanceAnswer30.NO, image: '' },
      { text: 'Yes', value: DoorDistanceAnswer30.YES, image: '' },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer, country }) => {
      if (answer === DoorDistanceAnswer30.NO) {
        return ICStepName.BadCompatibility;
      }

      // US users skip CylinderExchangePossible, measurements, and B2B
      if (country === 'us') {
        return ICStepName.RealEstateUsage;
      }

      return ICStepName.CylinderExchangePossible;
    },
  },
  // Door distance 35
  {
    stepName: ICStepName.DoorDistance35,
    question: ICQuestions.IC_SURVEY_DOOR_DISTANCE_35,
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.DOOR_DISTANCE,
    options: [
      { text: 'No', value: DoorDistanceAnswer35.NO, image: '' },
      { text: 'Yes', value: DoorDistanceAnswer35.YES, image: '' },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer, country }) => {
      if (answer === DoorDistanceAnswer35.NO) {
        return ICStepName.BadCompatibility;
      }

      if (country === 'us') {
        return ICStepName.RealEstateUsage;
      }

      return ICStepName.CylinderExchangePossible;
    },
  },
  // Door distance 40
  {
    stepName: ICStepName.DoorDistance40,
    question: ICQuestions.IC_SURVEY_DOOR_DISTANCE_40,
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.DOOR_DISTANCE,
    options: [
      { text: 'No', value: DoorDistanceAnswer40.NO, image: '' },
      { text: 'Yes', value: DoorDistanceAnswer40.YES, image: '' },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer, country }) => {
      if (answer === DoorDistanceAnswer40.NO) {
        return ICStepName.BadCompatibility;
      }

      if (country === 'us') {
        return ICStepName.RealEstateUsage;
      }

      return ICStepName.CylinderExchangePossible;
    },
  },
  // Cylinder exchange possible
  {
    stepName: ICStepName.CylinderExchangePossible,
    question: ICQuestions.IC_SURVEY_CYLINDER_EXCHANGE_POSSIBLE,
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.CYLINDER_EXCHANGE_POSSIBLE,
    options: [
      { text: 'No', value: CylinderExchangePossibleAnswer.NO },
      { text: 'Yes', value: CylinderExchangePossibleAnswer.YES },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ formValues, country }) => {
      const cylinderType = formValues?.icSurvey_cylinderType as CylinderTypeAnswer;
      const cylinderTypeNordics =
        formValues?.icSurvey_cylinderTypeNordics as CylinderTypeNordicsAnswer;
      const isExchangePossible =
        formValues?.icSurvey_cylinderExchangePossible as CylinderExchangePossibleAnswer;
      const isNordicsCylinder = [
        CylinderTypeNordicsAnswer.NORDIC_ROUND,
        CylinderTypeNordicsAnswer.SCANDIC_OVAL,
      ].includes(cylinderTypeNordics);

      // skip inside and outside cylinder length for UK, Nordics, US, and exchange not possible
      if (
        country === 'us' ||
        cylinderType === CylinderTypeAnswer.UK_OVAL ||
        isNordicsCylinder ||
        isExchangePossible === CylinderExchangePossibleAnswer.NO
      ) {
        return ICStepName.RealEstateUsage;
      }

      return ICStepName.InsideCylinderLength;
    },
  },
  // Inside cylinder length
  {
    stepName: ICStepName.InsideCylinderLength,
    question: ICQuestions.IC_SURVEY_INSIDE_CYLINDER_LENGTH,
    heading: 'Inside cylinder length',
    subtext: 'Inside cylinder length measure info',
    startingValue: 50,
    unit: 'mm',
    type: 'valueInput',
    image: IMAGE_PATHS.CYLINDER_INNER_LENGTH,
    button: { text: 'Next', value: 'next' },
    skippable: false,
    backNavigation: true,
    nextStep: async () => ICStepName.OutsideCylinderLength,
  },
  // Outside cylinder length
  {
    stepName: ICStepName.OutsideCylinderLength,
    question: ICQuestions.IC_SURVEY_OUTSIDE_CYLINDER_LENGTH,
    heading: 'Outside cylinder length',
    subtext: 'Outside cylinder length measure info',
    unit: 'mm',
    startingValue: 40,
    type: 'valueInput',
    image: IMAGE_PATHS.CYLINDER_OUTER_LENGTH,
    button: { text: 'Next', value: 'next' },
    skippable: false,
    backNavigation: true,
    nextStep: async () => ICStepName.RealEstateUsage,
  },
  // B2B steps
  {
    stepName: ICStepName.B2BProperties,
    question: ICQuestions.IC_SURVEY_B2B_PROPERTIES,
    type: 'imageSelect',
    options: [
      {
        text: 'Less than 50',
        value: B2BPropertiesAnswer.LESS_50,
        image: IMAGE_PATHS.PROPERTIES_1_TO_49,
      },
      {
        text: '50 or more',
        value: B2BPropertiesAnswer.MORE_50,
        image: IMAGE_PATHS.PROPERTIES_50_PLUS,
      },
    ],
    skippable: false,
    backNavigation: false,
    nextStep: async ({ answer, icCompleted, router, formValues }) => {
      if (answer === B2BPropertiesAnswer.MORE_50) {
        await icCompleted();
        router.push(paths.b2bContactForm);
        return undefined;
      }

      return getEuResultStep(formValues!);
    },
  },

  // Key dimensions
  {
    stepName: ICStepName.KeyDimensions,
    question: ICQuestions.IC_SURVEY_KEY_DIMENSIONS,
    description: 'keyDimensionsDescription',
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.KEY_DIMENSIONS,
    options: [
      { text: 'No', value: KeyDimensionsAnswer.NO },
      { text: 'Yes', value: KeyDimensionsAnswer.YES },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async () => ICStepName.DoorDirection,
  },

  // ========== US / Hermes Steps ==========

  // US: Single cylinder deadbolt
  {
    stepName: ICStepName.SingleCylinderDeadboltUS,
    question: ICQuestions.IC_SURVEY_US_SINGLE_CYLINDER,
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.US_SINGLE_CYLINDER,
    underImageText: 'explanationSingleCylinderDeadbolt',
    options: [
      { text: 'No', value: 'no' },
      { text: 'Yes', value: 'yes' },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      if (answer === 'yes') {
        return ICStepName.MultipleCylinderUS;
      }

      return ICStepName.MoreCylindersUS;
    },
  },
  // US: More cylinders type (replaces NonSupportedCylinders, adds euro option)
  {
    stepName: ICStepName.MoreCylindersUS,
    question: ICQuestions.IC_SURVEY_US_MORE_CYLINDERS,
    type: 'imageSelect',
    options: [
      {
        text: 'doubleCylinder',
        value: MoreCylindersUSAnswer.DOUBLE_CYLINDER,
        image: IMAGE_PATHS.US_DOUBLE_CYLINDER,
        underImageText: 'The door is unlocked from the outside and the inside with a key.',
      },
      {
        text: 'mortiseLock',
        value: MoreCylindersUSAnswer.MORTISE_LOCK,
        image: IMAGE_PATHS.US_MORTISE_LOCK,
        underImageText: 'A lock installed inside a pocket (mortise) cut into the door.',
      },
      {
        text: 'Euro',
        value: MoreCylindersUSAnswer.EURO,
        image: IMAGE_PATHS.EURO,
      },
      {
        text: 'other',
        value: MoreCylindersUSAnswer.OTHER,
        image: IMAGE_PATHS.US_OTHER,
      },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async ({ answer }) => {
      if (answer === MoreCylindersUSAnswer.EURO) {
        return ICStepName.LockType;
      }

      return ICStepName.MultipleCylinderUS;
    },
  },
  // US: Multiple cylinder deadbolts
  {
    stepName: ICStepName.MultipleCylinderUS,
    question: ICQuestions.IC_SURVEY_US_MULTIPLE_CYLINDERS,
    type: 'imageSelect',
    mainImage: IMAGE_PATHS.US_MULTIPLE_CYLINDER,
    options: [
      { text: 'Yes', value: 'yes' },
      { text: 'No', value: 'no' },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async () => ICStepName.LockManufacturerUS,
  },
  // US: Lock manufacturer
  {
    stepName: ICStepName.LockManufacturerUS,
    question: ICQuestions.IC_SURVEY_US_LOCK_MANUFACTURER,
    type: 'dropdownSelect',
    options: [
      { text: 'Arrow Hardware', value: USLockManufacturerAnswer.ARROW_HARDWARE },
      { text: 'Ashley Norton', value: USLockManufacturerAnswer.ASHLEY_NORTON },
      { text: 'Baldwin', value: USLockManufacturerAnswer.BALDWIN },
      { text: 'Callan', value: USLockManufacturerAnswer.CALLAN },
      { text: 'Defiant', value: USLockManufacturerAnswer.DEFIANT },
      { text: 'Delaney', value: USLockManufacturerAnswer.DELANEY },
      { text: 'Emtek', value: USLockManufacturerAnswer.EMTEK },
      { text: 'EZ Set', value: USLockManufacturerAnswer.EZ_SET },
      { text: 'Faultless', value: USLockManufacturerAnswer.FAULTLESS },
      { text: 'Gatehouse', value: USLockManufacturerAnswer.GATEHOUSE },
      { text: 'Global Door Controls', value: USLockManufacturerAnswer.GLOBAL_DOOR_CONTROLS },
      { text: 'Grandeur', value: USLockManufacturerAnswer.GRANDEUR },
      { text: 'Hager', value: USLockManufacturerAnswer.HAGER },
      { text: 'Kwikset', value: USLockManufacturerAnswer.KWIKSET },
      { text: 'Max Grade', value: USLockManufacturerAnswer.MAX_GRADE },
      { text: 'Omnia Industries', value: USLockManufacturerAnswer.OMNIA_INDUSTRIES },
      { text: 'Schlage', value: USLockManufacturerAnswer.SCHLAGE },
      { text: 'Stanley', value: USLockManufacturerAnswer.STANLEY },
      { text: 'Toledo Fine Locks', value: USLockManufacturerAnswer.TOLEDO_FINE_LOCKS },
      { text: 'Weiser', value: USLockManufacturerAnswer.WEISER },
      { text: 'Weslock', value: USLockManufacturerAnswer.WESLOCK },
      { text: 'Yale', value: USLockManufacturerAnswer.YALE },
      { text: 'Other', value: USLockManufacturerAnswer.OTHER },
    ],
    skippable: false,
    backNavigation: true,
    nextStep: async () => ICStepName.RealEstateUsage,
  },

  // ========== Compatibility Result Steps ==========

  // EU: Bad compatibility
  {
    stepName: ICStepName.BadCompatibility,
    question: ICQuestions.IC_SURVEY_BAD_COMPATIBILITY,
    mainText: ICQuestions.IC_SURVEY_BAD_COMPATIBILITY,
    type: 'compatibilityScreen',
    compatibility: Compatibility.BAD,
    subtext1: 'Your door does not currently meet the requirements for the Nuki Smart Lock',
    subtext2: 'However there is often an individual solution',
    image: IMAGE_PATHS.BAD_COMPATIBILITY,
    button1: { text: 'Start over', value: BadCompatibilityAnswer.START_OVER },
    button2: { text: 'findProPartner', value: BadCompatibilityAnswer.FIND_PRO_PARTNER },
    nextStep: async ({ answer, router }) => {
      if (answer === BadCompatibilityAnswer.START_OVER) {
        return ICStepName.Intro;
      }

      if (answer === BadCompatibilityAnswer.FIND_PRO_PARTNER) {
        router.push('https://pro.nuki.io/store-locator/');
      }

      return undefined;
    },
  },
  {
    stepName: ICStepName.GoodCompatibility,
    question: ICQuestions.IC_SURVEY_GOOD_COMPATIBILITY,
    mainText: ICQuestions.IC_SURVEY_GOOD_COMPATIBILITY,
    type: 'compatibilityScreen',
    compatibility: Compatibility.GOOD,
  },
  {
    stepName: ICStepName.RestrictedCompatibilityNordics,
    question: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_NORDICS,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_NORDICS,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
  {
    stepName: ICStepName.RestrictedCompatibilityUltraGo,
    question: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_ULTRA_GO,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_ULTRA_GO,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
  {
    stepName: ICStepName.RestrictedCompatibilityGo,
    question: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_GO,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_GO,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
  {
    stepName: ICStepName.RestrictedCompatibilityGoUK,
    question: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_GO_UK,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_GO_UK,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
  {
    stepName: ICStepName.RestrictedCompatibilityGoPro,
    question: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_GO_PRO,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_GO_PRO,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
  {
    stepName: ICStepName.RestrictedUltraCompatibility,
    question: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_ULTRA,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_COMPATIBILITY_ULTRA,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },

  // US: Bad compatibility
  {
    stepName: ICStepName.BadCompatibilityUS,
    question: ICStepName.BadCompatibilityUS,
    mainText: ICQuestions.IC_SURVEY_US_NOT_COMPATIBLE,
    type: 'compatibilityScreen',
    compatibility: Compatibility.BAD,
    subtext1: 'usBadCompatibilitySubtext1',
    subtext2: 'usBadCompatibilitySubtext2',
    image: IMAGE_PATHS.BAD_COMPATIBILITY_US,
    button1: { text: 'Start over', value: USBadCompatibilityAnswer.START_OVER },
    nextStep: async ({ answer }) => {
      if (answer === USBadCompatibilityAnswer.START_OVER) {
        return ICStepName.Intro;
      }

      return undefined;
    },
  },
  // US: Good compatibility (kept for backward compat)
  {
    stepName: ICStepName.GoodCompatibilityUS,
    question: ICQuestions.IC_SURVEY_US_NUKI_COMPATIBILITY_FULL,
    mainText: ICQuestions.IC_SURVEY_US_NUKI_COMPATIBILITY_FULL,
    type: 'compatibilityScreen',
    compatibility: Compatibility.GOOD,
  },
  // US: Restricted compatibility (multiple cylinders)
  {
    stepName: ICStepName.RestrictedCompatibilityUS,
    question: ICQuestions.IC_SURVEY_US_NUKI_COMPATIBILITY_RESTRICTED_SINGLE,
    mainText: ICQuestions.IC_SURVEY_US_NUKI_COMPATIBILITY_RESTRICTED_SINGLE,
    learnMore: 'Learn more',
    learnMoreLink: 'Learn more link',
    noteTitle: 'Note',
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
  // US: Restricted US (standard US result with Nuki SL)
  {
    stepName: ICStepName.RestrictedUs,
    question: ICQuestions.IC_SURVEY_RESTRICTED_US,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_US,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
  // US: Restricted US Euro (US user with euro cylinder, SL Ultra)
  {
    stepName: ICStepName.RestrictedUsEuro,
    question: ICQuestions.IC_SURVEY_RESTRICTED_US_EURO,
    mainText: ICQuestions.IC_SURVEY_RESTRICTED_US_EURO,
    type: 'compatibilityScreen',
    compatibility: Compatibility.RESTRICTED,
  },
];
